/**
 * @file businessMath.ts
 * @brief Pure helpers for Super Admin → Analytics (W22): the window sent to
 *        B25, money in the API's minor units, the FX line, and "–" + reason
 *        for every number the API leaves null.
 *
 * @details
 * Trust rule (DES / dispatcher 25-10-2026): show numbers exactly as the API
 * returns them. Nothing here rounds before display or turns a null into 0:
 * money is the API's minor value ÷ 100, with four decimals below £1 / $1 so
 * a 0.77p cost does not print as £0.01, and `exactMinor` gives the raw
 * value for a hover title.
 *
 * Window: `from` inclusive, `to` EXCLUSIVE (the start of the day after the
 * last day), cut on the viewer's own calendar and sent with their zone, as
 * the B25 contract asks. Up to 92 days is charted by day, longer by month
 * (B25 has no week buckets).
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import type { Currency, FxQuote, Money, Revenue } from "./types.ts";

// ----------------------------------------------------------------------
// Period → window
// ----------------------------------------------------------------------

export type PeriodKind = "7d" | "30d" | "90d" | "month" | "custom";

export interface Period {
  kind: PeriodKind;
  /** "YYYY-MM" when kind = month. */
  month?: string;
  /** Local first / last day (any time on that day) when kind = custom. */
  customFrom?: string;
  customTo?: string;
}

export interface BusinessQueryWindow {
  from: string;
  to: string;
  groupBy: "day" | "month";
}

const DAY_MS = 86_400_000;
/** Longest window charted by day; longer is by month. */
export const MAX_DAY_BUCKETS = 92;

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addLocalDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

function groupByFor(from: Date, to: Date): "day" | "month" {
  return (to.getTime() - from.getTime()) / DAY_MS > MAX_DAY_BUCKETS + 1 ? "month" : "day";
}

/** "YYYY-MM" of a date, local. */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** The last `count` months, newest first, as "YYYY-MM". */
export function recentMonths(now: Date, count = 12): string[] {
  return Array.from({ length: count }, (_, i) => monthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
}

/** Window for a period; null when a custom range is not picked yet. */
export function windowFor(period: Period, now: Date = new Date()): BusinessQueryWindow | null {
  const tomorrow = addLocalDays(startOfLocalDay(now), 1);
  let from: Date;
  let to: Date;
  switch (period.kind) {
    case "7d":
    case "30d":
    case "90d": {
      const days = period.kind === "7d" ? 7 : period.kind === "30d" ? 30 : 90;
      to = tomorrow;
      from = addLocalDays(tomorrow, -days);
      break;
    }
    case "month": {
      const [y, m] = (period.month ?? monthKey(now)).split("-").map(Number);
      if (!y || !m) return null;
      from = new Date(y, m - 1, 1);
      const next = new Date(y, m, 1);
      // This month stops at today: no empty future days on the charts.
      to = next.getTime() > tomorrow.getTime() ? tomorrow : next;
      if (from.getTime() >= to.getTime()) return null;
      break;
    }
    case "custom": {
      if (!period.customFrom || !period.customTo) return null;
      from = startOfLocalDay(new Date(period.customFrom));
      to = addLocalDays(startOfLocalDay(new Date(period.customTo)), 1);
      if (!(from.getTime() < to.getTime())) return null;
      break;
    }
    default:
      return null;
  }
  return { from: from.toISOString(), to: to.toISOString(), groupBy: groupByFor(from, to) };
}

/** "26 Sep – 25 Oct 2026" for a window (to is exclusive, so the last day is to − 1 day). */
export function windowLabel(w: { from: string; to: string }): string {
  const from = new Date(w.from);
  const last = new Date(new Date(w.to).getTime() - 1);
  const sameYear = from.getFullYear() === last.getFullYear();
  const d = (x: Date, year: boolean) =>
    x.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(year ? { year: "numeric" } : {}) });
  return `${d(from, !sameYear)} – ${d(last, true)}`;
}

// ----------------------------------------------------------------------
// Money (API minor units)
// ----------------------------------------------------------------------

export const CURRENCY_CODE: Record<Currency, string> = { gbp: "GBP", usd: "USD" };

/**
 * Minor units → "£1,234.56". Below one pound / dollar (and not 0), up to
 * four decimals ("£0.0077"), so a small cost is never shown as £0.01 or
 * £0.00. Null → "–".
 */
export function formatMoney(minor: number | null | undefined, currency: Currency | string): string {
  if (minor == null || !Number.isFinite(minor)) return "–";
  const code = currency.length === 3 && currency === currency.toUpperCase() ? currency : CURRENCY_CODE[currency as Currency] ?? currency.toUpperCase();
  const major = minor / 100;
  const small = major !== 0 && Math.abs(major) < 1;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: code,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: small ? 4 : 2,
  }).format(major);
}

/** Compact axis tick: "£1.2k", "£15", "£0.5". */
export function formatMoneyTick(minor: number, currency: Currency): string {
  const major = minor / 100;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: CURRENCY_CODE[currency],
    currencyDisplay: "narrowSymbol",
    notation: Math.abs(major) >= 1000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(major) >= 10 ? 0 : 2,
  }).format(major);
}

/** Hover title with the API's own number: "768.627 pence (exact, as the API returned it)". */
export function exactMinor(minor: number | null | undefined, currency: Currency | string): string {
  if (minor == null) return "";
  const unit = String(currency).toLowerCase() === "gbp" ? "pence" : String(currency).toLowerCase() === "usd" ? "US cents" : `${String(currency).toUpperCase()} minor units`;
  return `${minor} ${unit} (as the API returned it)`;
}

export function pick(m: Money | null | undefined, currency: Currency): number | null {
  if (!m) return null;
  const v = m[currency];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Revenue in other currencies that the API left out of gbp / usd: "EUR 12.00". */
export function notConvertedLines(rev: Pick<Revenue, "not_converted"> | null | undefined): string[] {
  if (!rev?.not_converted) return [];
  return Object.entries(rev.not_converted)
    .filter(([, v]) => Number(v) !== 0)
    .map(([cur, v]) => formatMoney(Number(v), cur.toUpperCase()));
}

// ----------------------------------------------------------------------
// Percentages and nulls
// ----------------------------------------------------------------------

/** 99.19 → "99.19%"; -12.5 → "−12.5%"; null → "–". */
export function formatPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "–";
  const s = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(Math.abs(v));
  return `${v < 0 ? "−" : ""}${s}%`;
}

export function formatCount(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "–";
  return new Intl.NumberFormat("en-GB").format(v);
}

/** Why each nullable number is null, in words for the screen (B25 §5). */
export const NULL_REASONS = {
  gross_margin_pct: "No revenue in this period, so a margin % can't be worked out.",
  conversion: "No sign-up in this period is old enough yet to count.",
  churn_default: "Plan endings are recorded from 21 Oct 2026; pick a period that starts on or after that day.",
  mrr_default: "A paying subscription's Stripe price couldn't be read, so MRR would be a guess.",
  unit_amount: "Stripe price couldn't be read.",
} as const;

// ----------------------------------------------------------------------
// FX line
// ----------------------------------------------------------------------

/**
 * The FX line every tab shows. "£ at 0.79 per $1 · fallback rate (live
 * rate unavailable)"; a live quote names its source and time.
 */
export function fxLabel(fx: FxQuote | null | undefined): string {
  if (!fx) return "";
  const rate = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 6 }).format(fx.usd_to_gbp);
  if (fx.source === "fallback_constant") return `£ at ${rate} per $1 · fallback rate (live rate unavailable)`;
  const when = fx.fetched_at
    ? `, fetched ${new Date(fx.fetched_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
    : "";
  return `£ at ${rate} per $1 · ${fx.source}${when}`;
}

// ----------------------------------------------------------------------
// Periods on charts ("YYYY-MM-DDTHH:MM:SS", zone-less, the viewer's calendar)
// ----------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function periodLabel(period: string, groupBy: "day" | "month"): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(period);
  if (!m) return period;
  const [, y, mo, d] = m;
  const name = MONTHS[Number(mo) - 1] ?? mo;
  return groupBy === "month" ? `${name} ${y}` : `${Number(d)} ${name}`;
}

// ----------------------------------------------------------------------
// Product / vendor vocabulary (labels; colour slot follows the entity)
// ----------------------------------------------------------------------

export const VENDOR_LABEL: Record<string, string> = {
  openai: "OpenAI",
  deepgram: "Deepgram",
  soniox: "Soniox",
  assemblyai: "AssemblyAI",
  azure: "Azure",
  google: "Google",
  anthropic: "Anthropic",
  unattributed: "Unattributed",
};

/** Fixed slot per vendor (never by rank). Unattributed is grey. */
export const VENDOR_COLOR: Record<string, string> = {
  openai: "var(--viz-1)",
  deepgram: "var(--viz-2)",
  soniox: "var(--viz-3)",
  assemblyai: "var(--viz-4)",
  azure: "var(--viz-5)",
  google: "var(--viz-6)",
  anthropic: "var(--viz-7)",
  unattributed: "var(--viz-other)",
};

export const COVERAGE_LABEL: Record<string, string> = {
  free: "Free users",
  plan: "Covered by a plan",
  charged: "Charged",
  other: "Other",
  not_applicable: "Not applicable",
};

export const COMPONENT_LABEL: Record<string, string> = {
  asr: "Speech to text",
  mt: "Translation",
  tts: "Voice",
  llm: "AI text",
  embedding: "Embeddings",
  unattributed: "Unattributed",
  pre_step_ledger: "Before step ledger",
};

export const PLAN_LABEL: Record<string, string> = {
  pro: "Pro",
  business: "Business",
  free: "Free",
  enterprise: "Enterprise",
};

export function planLabel(code: string): string {
  return PLAN_LABEL[code] ?? (code ? code.charAt(0).toUpperCase() + code.slice(1) : "–");
}

/** Vendors with any cost, largest first; unattributed always last. */
export function vendorsWithCost<T extends { vendor: string; cost: Money }>(rows: T[]): T[] {
  return rows
    .filter((r) => r.cost.usd !== 0)
    .sort((a, b) => (a.vendor === "unattributed" ? 1 : b.vendor === "unattributed" ? -1 : b.cost.usd - a.cost.usd));
}

// ----------------------------------------------------------------------
// Tabs (the open tab is the URL hash, as on Links & company info)
// ----------------------------------------------------------------------

export const ANALYTICS_TABS = ["revenue", "costs", "users", "subscribers", "activity"] as const;
export type AnalyticsTab = (typeof ANALYTICS_TABS)[number];

export function resolveAnalyticsTab(hash: string): AnalyticsTab {
  const id = hash.replace(/^#/, "");
  return (ANALYTICS_TABS as readonly string[]).includes(id) ? (id as AnalyticsTab) : "revenue";
}
